import { useViewport } from 'components/general/useViewPort';
import React, { useMemo } from "react";
import Competitor from 'components/presentation/Competitor';
import { NocMemo as Noc } from 'components/presentation/Noc';
import Label from "components/general/Label"
import { ColumnExpander, ColumnNameH2H } from "components/common/TableColumns"
import StatsBox from "components/sports/hoc/views/StatsBox"
import { dateFormat } from "helpers/utils"
import { findArray } from 'helpers/utils'
import LoaderSubComponent from "components/general/LoaderSubComponent";
import MirsTable from "components/general/table/Table";
import { capitalizeFirstLetter } from 'helpers/utils'
import { useUrlFriendlyToParameters } from "helpers/urlFriendly";

const TableColumns = () => {
  return (
    [
      ColumnBib({ disableSort: true, label: 'HeaderShirtNo2', headerClassName: 'text-end w-10' }),
      ColumnNameH2H({ showCaptainAbbr: true, showSuspendedCode: false, hideNoc: true }),
      ColumnPosition({}),
      ColumnExpander({ type: 'alwaysTrue', idNode: 'athlete' })
    ]
  )
}

export function ColumnBib({
  disableSort = false,
  className = 'text-end',
  headerClassName = 'text-end w-5 text-nowrap',
  label = 'HeaderBib',
  expecialCase = false,
  remove = false
}) {
  return (
    {
      Header: <Label data={label} />,
      accessor: 'bib',
      className: className,
      headerClassName: headerClassName,
      disableSortBy: disableSort,
      id: 'Bib',
      remove: remove,
      Cell: (props) => {
        let isSuspended = (findArray(props.cell.row.original.eventUnitEntries, { eue_code: ['STATUS'] }).eue_value === 'SUSPEND')
        if (isSuspended) {
          return '(S)'
        }
        return (expecialCase && props.value > 99999) ? '' : props.value
      }
    }
  )
}

export function ColumnPosition({
  disableSort = true,
  className = 'text-start',
  headerClassName = 'text-end w-10',
  label = 'PlayingPosition',
  code = 'Position'
}) {
  return (
    {
      Header: <Label data={label} />,
      accessor: 'functionDescription',
      className: className,
      headerClassName: headerClassName,
      disableSortBy: disableSort,
      id: code
    }
  )
}

const getConfigurationBox = (date) => {

  const formatedDate = date ? dateFormat(date, "", "D MMM H:mm") : "";

  let configuration = {
    header: `Current Tournament Statistics - ${formatedDate}`,
    stats: [
      {
        order: 1,
        code: 'TOTAL',
        attr: 'value',
        label: 'stat_1',
        title: 'Tournament Goals Scored'
      },
      {
        order: 2,
        code: 'CARDS',
        subCode: 'GC',
        attr: 'value',
        label: 'stat_2',
        title: 'Green Cards'
      },
      {
        order: 3,
        code: 'CARDS',
        subCode: 'YC',
        attr: 'value',
        label: 'stat_3',
        title: 'Yellow Cards'
      },
      {
        order: 4,
        code: 'CARDS',
        subCode: 'RC',
        attr: 'value',
        label: 'stat_4',
        title: 'Red Cards'
      },
      {
        order: 5,
        code: 'INTERNAT_PLAYED',
        attr: 'eue_value',
        label: 'stat_5',
        title: 'International Matches Played',
        eventUnitEntries: true
      },
      {
        order: 6,
        code: 'INTERNAT_GOALS',
        attr: 'eue_value',
        label: 'stat_6',
        title: 'International Goals Scored',
        eventUnitEntries: true
      }
    ]
  }
  return configuration;
}

const getConfiguration = (data, breakPoint) => {
  let configuration = {
    columns: [],
    subComponent: null,
    caption: null,
    hiddenColumns: [],
    sortBy: [],
    configs: {
    }
  };

  configuration.captionStarters = (
    <div className="d-flex justify-content-between">
      <div className='d-flex align-items-center'>
        <div className='d-none d-md-block me-1'>
          <Noc className='me-1' {...data?.participant?.organisation} />
        </div>
        <strong>
          <Competitor hidePicture={true} {...data?.participant} />
        </strong>
      </div>
      <div>
        <Uniform data={data} />
      </div>
    </div>
  )
  configuration.columns = TableColumns();

  configuration.captionSubstitutes = (

    <div className='d-flex align-items-center'>
      <div className='d-none d-md-block me-8'>
        <Label data="HeaderSubstitutes" />
      </div>
    </div>
  )

  return configuration;
}

const Uniform = ({ data }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters();
  const uniform = capitalizeFirstLetter(data?.eventUnitEntries?.find(i => i?.eue_code === 'UNIFORM')?.eue_value?.toLowerCase());
  const shorts = capitalizeFirstLetter(data?.eventUnitEntries?.find(i => i?.eue_code === 'SHORTS')?.eue_value?.toLowerCase());
  const socks = capitalizeFirstLetter(data?.eventUnitEntries?.find(i => i?.eue_code === 'SOCKS')?.eue_value?.toLowerCase());
  const uniformGk = capitalizeFirstLetter(data?.eventUnitEntries?.find(i => i?.eue_code === 'UNIFORM_GK')?.eue_value?.toLowerCase());
  const gender = data?.participant?.code?.substring(3, 4)
  return (
    <>
      <div>
        {uniform} <Label data="shirt" sport={mirs_discipline} />, {shorts} {gender === 'W' ? <Label data="skirt" sport={mirs_discipline} /> : <Label data="shorts" />}, {socks} <Label data="socks" sport={mirs_discipline} />
      </div>
      <div>
        <Label data="gkShirt" sport={mirs_discipline} />: {uniformGk}
      </div>
    </>
  )
}

const StartListSide = ({ data, resultStatus, config, date }) => {
  const { mirs_rsc } = useUrlFriendlyToParameters();
  const { breakPoint } = useViewport();

  const configEvent = useMemo(() => getConfiguration(data, breakPoint));

  const configBox = useMemo(() => getConfigurationBox(date));

  const renderRowSubComponent = React.useCallback(({ row }) => (
    <LoaderSubComponent component={
      <StatsBox data={row?.original} config={configBox} breakPoint={breakPoint} />
    }
    />
  ), [resultStatus]);

  const starters = data?.teamAthletes?.filter(i => i?.eventUnitEntries?.find(eui => eui?.eue_code === 'STARTER' && eui?.eue_value === 'Y'))
  const substitutes = data?.teamAthletes?.filter(i => !i?.eventUnitEntries?.find(eui => eui?.eue_code === 'STARTER' && eui?.eue_value === 'Y'))

  return (<>
    {data.teamAthletes && (
      <div className="start-list-side-main">
        <div className="row">
          <div className="col-12">
            <MirsTable
              className={"table-start-list"}
              id={`mirs-table-start-list-starters-${mirs_rsc}-${data?.participant?.code}`}
              data={starters}
              columns={configEvent.columns}
              renderRowSubComponent={renderRowSubComponent}
              keyRow='athlete-code'
              caption={configEvent.captionStarters}
              initialSortBy={configEvent.sortBy}
            />
          </div>
          <div className="col-12">
            <MirsTable
              className={"table-start-list"}
              id={`mirs-table-start-list-substitutes-${mirs_rsc}-${data?.participant?.code}`}
              data={substitutes}
              columns={configEvent.columns}
              renderRowSubComponent={renderRowSubComponent}
              keyRow='athlete-code'
              caption={configEvent.captionSubstitutes}
              initialSortBy={configEvent.sortBy}
            />
          </div>
        </div>
      </div>
    )
    }
  </>)
}

export default StartListSide;
